/**
 * What the External Data Transfer Specification panel says about sources the
 * study does not use, how the safety database's data arrive, and a
 * generation that left sections out. Shapes mirror
 * functions/src/models/edtsTypes.ts (ExcludedSource, DetectedSource,
 * EDTSDocument).
 */
import { humanise } from './sectionStatus';

export interface ExcludedSourceLike {
  type: string;
  displayName?: string;
  /** A sentence; older or hand-edited records may hold a list. */
  reason?: unknown;
}

/** Each source type in plain words (functions ExternalDataSource). */
export const SOURCE_TYPE_LABELS: Record<string, string> = {
  central_lab: 'Central laboratory',
  irt: 'Randomization and drug supply system',
  epro: 'Electronic patient-reported outcomes',
  imaging: 'Imaging core laboratory',
  safety_db: 'Safety database',
  biomarker: 'Biomarker laboratory',
  wearables: 'Wearables and connected devices',
  ehr: 'Electronic health records',
};

export function sourceTypeLabel(type: string | null | undefined): string {
  if (!type) return '';
  return SOURCE_TYPE_LABELS[type] ?? humanise(type);
}

const TRANSFER_MODES: Record<string, string> = {
  reconciliation_only: 'Reconciliation only, no transfer file',
  file_transfer: 'File transfer',
};

export function transferModeLabel(mode: string | null | undefined): string | undefined {
  if (!mode) return undefined;
  return TRANSFER_MODES[mode] ?? humanise(mode);
}

/**
 * The badge in the Origin column. The generator writes `confirmation` on each
 * source (functions/src/edts/edtsSources.ts): 'confirmed' when Study Settings
 * says the study uses it, 'to_be_confirmed' when it was only found in the
 * protocol. Records written before that field carry only origin / evidence.
 */
export function sourceOriginBadge(src: {
  confirmation?: string | null;
  origin?: string | null;
  evidence?: string | null;
}): { label: string; tone: 'pending' | 'user' | 'detected' } {
  if (src.confirmation === 'to_be_confirmed') return { label: 'To be confirmed', tone: 'pending' };
  if (src.origin === 'user' || src.evidence === 'User-specified') return { label: 'User', tone: 'user' };
  return { label: 'Detected', tone: 'detected' };
}

const sentence = (s: string) => {
  const t = s.trim();
  if (!t) return t;
  const c = t.charAt(0).toUpperCase() + t.slice(1);
  return /[.!?]$/.test(c) ? c : `${c}.`;
};

/** The stored reason as text: strings only, never "[object Object]". */
function reasonText(raw: unknown): string {
  const one = (r: unknown): string => {
    if (typeof r === 'string') return sentence(r);
    if (r && typeof r === 'object') {
      const o = r as Record<string, unknown>;
      for (const k of ['reason', 'text', 'message']) if (typeof o[k] === 'string') return sentence(o[k] as string);
    }
    return '';
  };
  const list = Array.isArray(raw) ? raw : [raw];
  return list.map(one).filter(Boolean).join(' ');
}

export function excludedSourceLine(s: ExcludedSourceLike): string {
  const name = (typeof s.displayName === 'string' && s.displayName.trim()) || sourceTypeLabel(s.type) || 'A source';
  return `${name}: ${reasonText(s.reason) || 'No reason was recorded.'}`;
}

/** The EDTS panel's own button. */
const REGENERATE_BUTTON = 'Regenerate All Sections';

/**
 * The notice for a specification stored as incomplete. Used by the EDTS, MCS
 * and SDTM panels (docgen reasons, 2026-10-01); `button` names the panel's
 * own regenerate button.
 */
export function generationStatusNotice(
  doc: {
    generationStatus?: string | null;
    failedSectionCount?: number | null;
  },
  button: string = REGENERATE_BUTTON,
): string | undefined {
  if (!doc.generationStatus || doc.generationStatus === 'complete') return undefined;
  const n = doc.failedSectionCount;
  if (typeof n === 'number' && n > 0) {
    return n === 1
      ? `This specification is incomplete: 1 section could not be generated. Use ${button} to try it again.`
      : `This specification is incomplete: ${n} sections could not be generated. Use ${button} to try them again.`;
  }
  return `This specification is incomplete: some sections could not be generated. Use ${button} to try them again.`;
}
